// src/components/FounderBadge.jsx
// Selo premium inspirado no comportamento de tilt/glare do React Bits.

import React, { useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";

import "../sass/components/founderBadge.scss";

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), max);

const getFounderYear = (value) => {
  const date = value ? new Date(value) : null;

  if (date && !Number.isNaN(date.getTime())) {
    return date.getFullYear();
  }

  return new Date().getFullYear();
};

const FounderBadge = ({
  concedidoEm = null,
  origem = null,
  className = "",
}) => {
  const ref = useRef(null);
  const frameRef = useRef(null);

  const targetRef = useRef({
    rotateX: 0,
    rotateY: 0,
    x: 50,
    y: 50,
  });

  const currentRef = useRef({
    rotateX: 0,
    rotateY: 0,
    x: 50,
    y: 50,
  });

  const founderYear = getFounderYear(concedidoEm);

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const coarsePointer = window.matchMedia(
      "(pointer: coarse)"
    ).matches;

    if (reducedMotion || coarsePointer) {
      return undefined;
    }

    const tick = () => {
      const target = targetRef.current;
      const current = currentRef.current;

      current.rotateX +=
        (target.rotateX - current.rotateX) * 0.13;
      current.rotateY +=
        (target.rotateY - current.rotateY) * 0.13;
      current.x +=
        (target.x - current.x) * 0.16;
      current.y +=
        (target.y - current.y) * 0.16;

      element.style.setProperty(
        "--founder-rx",
        `${current.rotateX}deg`
      );
      element.style.setProperty(
        "--founder-ry",
        `${current.rotateY}deg`
      );
      element.style.setProperty(
        "--founder-x",
        `${current.x}%`
      );
      element.style.setProperty(
        "--founder-y",
        `${current.y}%`
      );

      frameRef.current = window.requestAnimationFrame(tick);
    };

    frameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const handlePointerMove = (event) => {
    const element = ref.current;

    if (!element) return;

    const rect = element.getBoundingClientRect();

    const x = clamp(
      (event.clientX - rect.left) / rect.width,
      0,
      1
    );

    const y = clamp(
      (event.clientY - rect.top) / rect.height,
      0,
      1
    );

    targetRef.current = {
      rotateX: (0.5 - y) * 6,
      rotateY: (x - 0.5) * 8,
      x: x * 100,
      y: y * 100,
    };

    element.classList.add("is-active");
  };

  const handlePointerLeave = () => {
    targetRef.current = {
      rotateX: 0,
      rotateY: 0,
      x: 50,
      y: 50,
    };

    ref.current?.classList.remove("is-active");
  };

  const originLabel =
    origem === "LANCAMENTO"
      ? "Primeira geração"
      : "Founder Edition";

  return (
    <span
      ref={ref}
      className={`psy-founder-badge ${className}`.trim()}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      title={`Membro Fundador • ${originLabel} • ${founderYear}`}
      aria-label={`Membro Fundador PsyAssist Pro, ${founderYear}`}
    >
      <span
        className="psy-founder-badge__spectrum"
        aria-hidden="true"
      />

      <span
        className="psy-founder-badge__glare"
        aria-hidden="true"
      />

      <span className="psy-founder-badge__icon">
        <Sparkles size={14} strokeWidth={2.2} />
      </span>

      <span className="psy-founder-badge__copy">
        <strong>Membro Fundador</strong>
        <small>{originLabel} • {founderYear}</small>
      </span>

      <span
        className="psy-founder-badge__serial"
        aria-hidden="true"
      >
        F
      </span>
    </span>
  );
};

export default FounderBadge;
